/**
 * FE-owned BOM Excel parse (3c-M) — column map confirmed from MP560.xlsm:
 * Wood A–P; Hardware A–I on Sheet1 (cột Loại → assembled/kd). No ISTA in file (3c-R).
 *
 * Sheet2 không được dùng làm nguồn KD — chưa có file mẫu thật để verify cấu trúc.
 * Nếu sau này có sản phẩm KD thật, xác nhận lại cấu trúc Sheet2 trước khi thêm
 * logic đọc từ đó. Cơ chế assembly DUY NHẤT đã verify: inferAssemblyFromKind
 * theo cột "Loại" trên bảng ngũ kim Sheet1.
 */
import * as XLSX from 'xlsx'
import { readSafeWorkbook } from '@/lib/safeWorkbook'
import type { ParsedBomIntakeLine, ParsedBomWorkbook } from './types'

type Matrix = unknown[][]

function cellStr(value: unknown): string {
  if (value == null || value === '') return ''
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number.isInteger(value) ? String(value) : String(value)
  }
  return String(value).replace(/\s+/g, ' ').trim()
}

/** Empty cells (incl. SheetJS defval:'') → null, never 0. */
function cellNum(value: unknown): number | null {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const trimmed = String(value).trim()
  if (!trimmed) return null
  const cleaned = trimmed.replace(',', '.').replace(/[^\d.-]/g, '')
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') {
    return null
  }
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

function norm(text: string): string {
  return text.normalize('NFC').toLowerCase().replace(/\s+/g, ' ').trim()
}

function looksLike(text: string, ...needles: string[]): boolean {
  const n = norm(text)
  return needles.some((needle) => n.includes(norm(needle)))
}

function rowJoin(row: unknown[]): string {
  return (row ?? []).map((c) => cellStr(c)).join(' | ')
}

function sheetToMatrix(ws: XLSX.WorkSheet): Matrix {
  return XLSX.utils.sheet_to_json(ws, {
    header: 1,
    defval: '',
    raw: true,
    blankrows: true,
  }) as Matrix
}

function isWoodHeader(row: unknown[]): boolean {
  const text = rowJoin(row)
  return (
    looksLike(text, 'tên chi tiết') &&
    (looksLike(text, 'loại gỗ') || looksLike(text, 'loai go'))
  )
}

function isHardwareHeader(row: unknown[]): boolean {
  if (isPackagingHeader(row)) return false
  const text = rowJoin(row)
  return (
    looksLike(text, 'tên vật tư') &&
    (looksLike(text, 'quy cách') || looksLike(text, 'số lượng'))
  )
}

function isSubHeaderRow(row: unknown[]): boolean {
  const text = rowJoin(row)
  return looksLike(text, 'dày') && looksLike(text, 'rộng') && looksLike(text, 'dài')
}

function isFooterRow(row: unknown[]): boolean {
  return looksLike(rowJoin(row), 'người lập', 'người kiểm tra')
}

function inferAssemblyFromKind(kind: string): 'assembled' | 'kd' | null {
  const k = norm(kind)
  if (!k) return null
  if (k.includes('kd') || k.includes('tháo') || k.includes('thao')) return 'kd'
  if (k.includes('sx') || k.includes('ráp') || k.includes('rap')) return 'assembled'
  return null
}

function parseWood(matrix: Matrix): ParsedBomIntakeLine[] {
  const headerIdx = matrix.findIndex(isWoodHeader)
  if (headerIdx < 0) return []
  let dataStart = headerIdx + 1
  if (isSubHeaderRow(matrix[dataStart] ?? [])) dataStart += 1
  if (isWoodHeader(matrix[dataStart] ?? [])) dataStart += 1
  if (isSubHeaderRow(matrix[dataStart] ?? [])) dataStart += 1

  const lines: ParsedBomIntakeLine[] = []
  for (let i = dataStart; i < matrix.length; i += 1) {
    const row = matrix[i] ?? []
    if (
      isHardwareHeader(row) ||
      isPackagingHeader(row) ||
      isFooterRow(row) ||
      looksLike(rowJoin(row), 'vật tư sản xuất')
    ) {
      break
    }
    const part_name = cellStr(row[1])
    if (!part_name) continue
    if (looksLike(part_name, 'tổng', 'stt', 'tên chi tiết')) continue
    // Keep rows with invalid qty — server staging marks ERROR (do not drop silently)
    const qty_per = cellNum(row[10])

    lines.push({
      line_kind: 'wood',
      raw_data: {
        sort_order: cellNum(row[0]) ?? lines.length + 1,
        part_name,
        wood_type_raw: cellStr(row[2]) || null,
        dims_finished_t: cellNum(row[3]),
        dims_finished_w: cellNum(row[4]),
        dims_finished_l: cellNum(row[5]),
        tenon: cellStr(row[6]) || null,
        dims_rough_t: cellNum(row[7]),
        dims_rough_w: cellNum(row[8]),
        dims_rough_l: cellNum(row[9]),
        qty_per,
        notes: cellStr(row[12]) || null,
        veneer_spec: cellStr(row[13]) || null,
        m3_finished: cellNum(row[14]),
      },
    })
  }
  return lines
}

function parseHardwareTable(
  matrix: Matrix,
  headerIdx: number,
): ParsedBomIntakeLine[] {
  const lines: ParsedBomIntakeLine[] = []
  for (let i = headerIdx + 1; i < matrix.length; i += 1) {
    const row = matrix[i] ?? []
    if (
      isHardwareHeader(row) ||
      isWoodHeader(row) ||
      isPackagingHeader(row) ||
      isFooterRow(row) ||
      looksLike(rowJoin(row), 'vật tư sản xuất')
    ) {
      break
    }
    const name_raw = cellStr(row[1])
    if (!name_raw) continue
    if (looksLike(name_raw, 'stt', 'tên vật tư')) continue
    // Keep rows with invalid qty — server staging marks ERROR (do not drop silently)
    const qty_per = cellNum(row[3])
    const kind = cellStr(row[7])
    const assembly = inferAssemblyFromKind(kind)

    lines.push({
      line_kind: 'hardware',
      packaging_assembly_type: assembly,
      packaging_ista_level: null, // 3c-R: never default ISTA
      raw_data: {
        sort_order: cellNum(row[0]) ?? lines.length + 1,
        name_raw,
        spec_raw: cellStr(row[2]) || null,
        qty_per,
        uom_hint: cellStr(row[5]) || null,
        color_hint: cellStr(row[6]) || null,
        kind_raw: kind || null,
        notes: cellStr(row[8]) || null,
      },
    })
  }
  return lines
}

function isPackagingHeader(row: unknown[]): boolean {
  const text = rowJoin(row)
  return (
    looksLike(text, 'đóng gói', 'dong goi', 'vật tư đóng gói', 'thùng carton', 'bao bì') &&
    (looksLike(text, 'tên vật tư') || looksLike(text, 'tên chi tiết') || looksLike(text, 'quy cách') || looksLike(text, 'số lượng') || looksLike(text, 'vật tư'))
  )
}

function parsePackagingTable(
  matrix: Matrix,
  headerIdx: number,
): ParsedBomIntakeLine[] {
  const lines: ParsedBomIntakeLine[] = []
  for (let i = headerIdx + 1; i < matrix.length; i += 1) {
    const row = matrix[i] ?? []
    if (
      isHardwareHeader(row) ||
      isWoodHeader(row) ||
      isPackagingHeader(row) ||
      isFooterRow(row) ||
      looksLike(rowJoin(row), 'vật tư sản xuất')
    ) {
      break
    }
    const name_raw = cellStr(row[1])
    if (!name_raw) continue
    if (looksLike(name_raw, 'stt', 'tên vật tư', 'vật tư đóng gói')) continue
    const qty_per = cellNum(row[3])

    lines.push({
      line_kind: 'packaging',
      raw_data: {
        sort_order: cellNum(row[0]) ?? lines.length + 1,
        name_raw,
        spec_raw: cellStr(row[2]) || null,
        qty_per,
        uom_hint: cellStr(row[4]) || cellStr(row[5]) || null,
        notes: cellStr(row[5]) || cellStr(row[6]) || null,
      },
    })
  }
  return lines
}

function extractCodeHint(wb: XLSX.WorkBook, matrices: Matrix[]): string {
  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name]
    for (const addr of ['E6', 'E2', 'D2']) {
      const cell = ws[addr] as XLSX.CellObject | undefined
      const v = cellStr(cell?.w ?? cell?.v)
      if (/^MP\d+/i.test(v)) return v.toUpperCase()
    }
  }
  for (const matrix of matrices) {
    for (const row of matrix.slice(0, 40)) {
      for (const c of (row ?? []).slice(0, 8)) {
        const v = cellStr(c)
        if (/^MP\d+/i.test(v)) return v.toUpperCase()
      }
    }
  }
  return ''
}

function extractNameHint(matrices: Matrix[], code: string): string {
  for (const matrix of matrices) {
    for (const row of matrix.slice(0, 15)) {
      for (const c of row ?? []) {
        const v = cellStr(c)
        if (looksLike(v, 'cabinet', 'tủ', 'bàn', 'ghế') && v.length < 80) {
          return v
        }
      }
    }
  }
  return code ? `BOM ${code}` : 'BOM Intake'
}

export function parseBomExcelForIntake(
  data: ArrayBuffer | Uint8Array,
): ParsedBomWorkbook {
  const wb = readSafeWorkbook(data, { cellDates: true })
  const matrices = wb.SheetNames.map((name) => sheetToMatrix(wb.Sheets[name]))
  const code_hint = extractCodeHint(wb, matrices)
  const name_hint = extractNameHint(matrices, code_hint)

  const lines: ParsedBomIntakeLine[] = []

  for (const matrix of matrices) {
    if (!lines.some((l) => l.line_kind === 'wood')) {
      lines.push(...parseWood(matrix))
    }
    for (let r = 0; r < matrix.length; r += 1) {
      if (isPackagingHeader(matrix[r] ?? [])) {
        lines.push(...parsePackagingTable(matrix, r))
        continue
      }
      if (!isHardwareHeader(matrix[r] ?? [])) continue
      // Sheet1 hardware tables only (via header detect). Sheet2 not used as KD source —
      // chưa có file mẫu thật để verify cấu trúc. Nếu sau này có sản phẩm KD thật,
      // xác nhận lại cấu trúc Sheet2 trước khi thêm logic đọc từ đó.
      lines.push(...parseHardwareTable(matrix, r))
    }
  }

  const hasAssembled = lines.some(
    (l) => l.line_kind === 'hardware' && l.packaging_assembly_type === 'assembled',
  )
  const hasKd = lines.some(
    (l) => l.line_kind === 'hardware' && l.packaging_assembly_type === 'kd',
  )
  const modes_detected: ('assembled' | 'kd')[] = []
  if (hasAssembled) modes_detected.push('assembled')
  if (hasKd) modes_detected.push('kd')
  if (modes_detected.length === 0) {
    modes_detected.push('assembled')
  }

  return {
    code_hint,
    name_hint,
    sheet_names: wb.SheetNames,
    modes_detected,
    lines,
  }
}
